import { ReconcileAction } from './enums';

/**
 * Normalize medication name for “human-like” matching.
 * - lowercase
 * - collapse multiple spaces
 */
export function normalizeMedName(name = '') {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Index an array by a derived key (e.g., normalized name).
 * @param {Array<any>} list
 * @param {(x:any)=>string} keyFn
 */
export function indexBy(list, keyFn) {
  const map = new Map();
  for (const item of list || []) {
    const k = keyFn(item);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(item);
  }
  return map;
}

/**
 * Compare active vs external by normalized name.
 * Returns items with suggested action: CONTINUE / MODIFY / STOP / NEW.
 * - CONTINUE: present in both and dose+frequency match.
 * - MODIFY: present in both but dose or frequency differ.
 * - STOP: only in active.
 * - NEW: only in external.
 * @param {Array<Med>} active
 * @param {Array<MedExternal>} external
 * @returns {Array<ReconciledItem>}
 */
export function reconcileHeuristics(active = [], external = []) {
  const byNameActive = indexBy(active, (m) => normalizeMedName(m.name));
  const byNameExternal = indexBy(external, (m) => normalizeMedName(m.name));

  const names = new Set([
    ...Array.from(byNameActive.keys()),
    ...Array.from(byNameExternal.keys()),
  ]);

  const items = [];

  for (const n of names) {
    const a = (byNameActive.get(n) || [])[0] || null;
    const e = (byNameExternal.get(n) || [])[0] || null;

    if (a && e) {
      const sameDose = (a.dose || '') === (e.dose || '');
      const sameFreq = (a.frequency || '') === (e.frequency || '');
      items.push({
        key: n,
        name: a.name || e.name,
        action: sameDose && sameFreq ? ReconcileAction.CONTINUE : ReconcileAction.MODIFY,
        from: { dose: a.dose, route: a.route, frequency: a.frequency },
        to:   { dose: e.dose, route: e.route, frequency: e.frequency },
        sourceIds: { activeId: a.id, externalId: e.id },
        warnings: buildWarnings(a, e),
        reason: sameDose && sameFreq
          ? 'Exact match for dose and frequency.'
          : 'Dose/Frequency differ between lists.',
      });
    } else if (a && !e) {
      // Activo sin contraparte externa → tratamiento en curso, continuar por defecto
      items.push({
        key: n,
        name: a.name,
        action: ReconcileAction.CONTINUE,
        from: { dose: a.dose, route: a.route, frequency: a.frequency },
        to:   { dose: a.dose, route: a.route, frequency: a.frequency },
        sourceIds: { activeId: a.id, externalId: null },
        warnings: [],
        reason: 'Ongoing treatment — no external counterpart.',
      });
    } else if (!a && e) {
      items.push({
        key: n,
        name: e.name,
        action: ReconcileAction.NEW,
        from: null,
        to:   { dose: e.dose, route: e.route, frequency: e.frequency },
        sourceIds: { activeId: null, externalId: e.id },
        warnings: [],
        reason: 'New in external list, not found in active list.',
      });
    }
  }

  // Sort: conflicts first (MODIFY), then NEW, STOP, CONTINUE
  const order = {
    [ReconcileAction.MODIFY]: 0,
    [ReconcileAction.NEW]: 1,
    [ReconcileAction.STOP]: 2,
    [ReconcileAction.CONTINUE]: 3,
  };
  return items.sort(
    (x, y) => order[x.action] - order[y.action] || x.name.localeCompare(y.name)
  );
}

/**
 * Build simple (mocked) warnings to highlight differences.
 */
function buildWarnings(a, e) {
  const ws = [];
  if (a && e) {
    if (a.dose !== e.dose) ws.push('Dose change');
    if (a.frequency !== e.frequency) ws.push('Frequency change');
    if ((a.route || '') !== (e.route || '')) ws.push('Route change');
  }
  return ws;
}

/**
 * Return a mocked “AI suggestion” derived from the heuristic.
 * @param {string} patientId
 * @param {Array<Med>} active
 * @param {Array<MedExternal>} external
 */
export function makeAISuggestion(patientId, active, external) {
  return {
    patientId,
    items: reconcileHeuristics(active, external),
    generatedAt: new Date().toISOString(),
    model: 'mock-gpt-suggestion',
    rationale:
      'Suggestions generated by a local heuristic (AI simulation). Exact matches → CONTINUE; discrepancies → MODIFY; only active → STOP; only external → NEW.',
  };
}
